import React, { createContext, useMemo, useState, type ReactNode } from 'react';

type DrawerProviderProps = {
  children: ReactNode;
};

type SnackbarSeverity = 'error' | 'warning' | 'info' | 'success';

interface DialogData {
  title: string;
  content: ReactNode;
}

type DataContextType = {
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDrawerOpen: () => void;
  toggleDrawer: () => void;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: SnackbarSeverity;
  handleSnackbarOpen: (message: string, severity: SnackbarSeverity) => void;
  handleSnackbarClose: () => void;
  dialogOpen: boolean;
  dialogData: DialogData;
  handleDialogOpen: (title: string, content: ReactNode) => void;
  handleDialogClose: () => void;
  backdropOpen: boolean;
  handleBackdropOpen: () => void;
  handleBackdropClose: () => void;
};

export const DataContext = createContext<DataContextType>({
  drawerOpen: false,
  setDrawerOpen: () => {},
  handleDrawerOpen: () => {},
  toggleDrawer: () => {},
  snackbarOpen: false,
  snackbarMessage: '',
  snackbarSeverity: 'success',
  handleSnackbarOpen: () => {},
  handleSnackbarClose: () => {},
  dialogOpen: false,
  dialogData: { title: '', content: null },
  handleDialogOpen: () => {},
  handleDialogClose: () => {},
  backdropOpen: false,
  handleBackdropOpen: () => {},
  handleBackdropClose: () => {},
});

export const DataContextProvider: React.FC<DrawerProviderProps> = ({
  children,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<DialogData>({
    title: '',
    content: null,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarSeverity>('success');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const handleBackdropOpen = () => setBackdropOpen(true);
  const handleBackdropClose = () => setBackdropOpen(false);

  const handleDrawerOpen = () => setDrawerOpen(true);
  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  const handleSnackbarOpen = (message: string, severity: SnackbarSeverity) => {
    setSnackbarOpen(true);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDialogOpen = (title: string, content: ReactNode) => {
    setDialogData({ title, content });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogData({ title: '', content: null });
  };

  return (
    <DataContext.Provider
      value={useMemo(
        () => ({
          drawerOpen,
          handleDrawerOpen,
          setDrawerOpen,
          toggleDrawer,
          snackbarOpen,
          snackbarMessage,
          snackbarSeverity,
          handleSnackbarOpen,
          handleSnackbarClose,
          dialogOpen,
          dialogData,
          handleDialogOpen,
          handleDialogClose,
          backdropOpen,
          handleBackdropOpen,
          handleBackdropClose,
        }),
        [
          drawerOpen,
          handleDrawerOpen,
          toggleDrawer,
          setDrawerOpen,
          snackbarOpen,
          snackbarMessage,
          snackbarSeverity,
          handleSnackbarOpen,
          handleSnackbarClose,
          dialogOpen,
          dialogData,
          handleDialogOpen,
          handleDialogClose,
          backdropOpen,
          handleBackdropOpen,
          handleBackdropClose,
        ],
      )}>
      {children}
    </DataContext.Provider>
  );
};
